/* eslint-disable no-nested-ternary */
import { Box, Typography } from '@mui/material'
import React, { MutableRefObject, useCallback, useEffect, useRef } from 'react'
import { trackWindowScroll } from 'react-lazy-load-image-component'
import 'swiper/css'
import 'swiper/css/navigation'

import { useTranslation } from 'react-i18next'
import { useVODStore } from '@/shared/store'
import { continueWatchingKeyword } from '@/shared/constants'
import VodItem from '@/features/VOD/vodClasses/vodItem'
import styled from 'styled-components'

import {
    FocusContext,
    useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import VodRowItemTV from './VodRowItemTV'
import { VodRowTVProps } from '@/appTV/VOD/types/vodRowTVTypes'
import { useItemFetcher } from '@/features/VOD/hooks/useItemFetch'
import { getUseVodItems } from '@/shared/getContainer/getUseVodItems'
import VodContentSkeleton from '@/features/VOD/components/skeletons/VodContentSkeleton'
import {
    DIRECTION_LEFT_FOCUS_KEY,
    DIRECTION_RIGHT_FOCUS_KEY,
} from '@/shared/constants/focusLibraryConstants'

const useVodItems = await getUseVodItems()

const VodRowViewScrollingWrapper = styled.div`
    overflow-x: hidden;
    overflow-y: hidden;
    flex-shrink: 1;
    flex-grow: 1;
    //padding-left: 60px;
`
const VodRowViewScrollingContent = styled.div`
    display: flex;
    flex-direction: row;
`

const VodRowTV = ({
    productId,
    title,
    handleGoToCategoryButtonClick,
    scrollPosition,
    maxHeight,
    hideCaptions,
    hideProgress,
    hideTitle,
    cardRadiusDp,
    rowHeightPercentage,
    isHomeContentRow,
    onFocusProp,
    onReady,
}: VodRowTVProps) => {
    const { t: translate } = useTranslation()
    const itemsContainerRef = useRef<HTMLDivElement>(null)
    const vodItemHeight = maxHeight

    const { rowItems, getItems, setRowItems, isLoading } =
        useVodItems(productId)

    const { isFetchContinuesWatching, setIsFetchContinuesWatching } =
        useVODStore()

    const { ref, focusKey } = useFocusable({
        onFocus: onFocusProp,
        isFocusBoundary: true,
        focusBoundaryDirections: [
            DIRECTION_LEFT_FOCUS_KEY,
            DIRECTION_RIGHT_FOCUS_KEY,
        ],
    })
    const scrollingRef: MutableRefObject<HTMLDivElement | null> = useRef(null)
    const animationRef = useRef<number | null>(null)
    const scrollTargetRef = useRef(0)
    const scrollStartRef = useRef(0)
    const scrollStartTimeRef = useRef<number | null>(null)

    useItemFetcher(productId, setRowItems, getItems)

    const customScrollTo = (targetPosition: number, duration: number) => {
        if (!scrollingRef.current) return

        scrollStartRef.current = scrollingRef.current.scrollLeft
        scrollTargetRef.current = targetPosition
        scrollStartTimeRef.current = null

        if (animationRef.current) return

        const animationStep = (currentTime: number) => {
            if (!scrollingRef.current) return
            if (scrollStartTimeRef.current === null)
                scrollStartTimeRef.current = currentTime

            const distance = scrollTargetRef.current - scrollStartRef.current
            const timeElapsed = currentTime - scrollStartTimeRef.current
            const progress = Math.min(timeElapsed / duration, 1) // Cap progress at 1

            // Ease-in-out function for smooth scrolling effect
            const easeInOutQuad = (t: number): number =>
                t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2

            const newScrollPosition =
                scrollStartRef.current + distance * easeInOutQuad(progress)
            scrollingRef.current.scrollLeft = newScrollPosition

            if (timeElapsed < duration) {
                animationRef.current = requestAnimationFrame(animationStep)
            } else {
                scrollingRef.current.scrollLeft = scrollTargetRef.current
                animationRef.current = null
            }
        }
        animationRef.current = requestAnimationFrame(animationStep)
    }

    const onAssetFocus = useCallback(
        ({ x }: { x: number }) => {
            customScrollTo(x, 500)
        },
        [scrollingRef]
    )

    useEffect(
        () => () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current)
        },
        []
    )

    useEffect(() => {
        const rowElement = itemsContainerRef.current // scrollingRef? What does it do?

        if (rowElement) {
            rowElement.addEventListener('scroll', () => {
                getItems()
            })
            return () =>
                rowElement.removeEventListener('scroll', () => getItems())
        }
    })

    useEffect(() => {
        if (isFetchContinuesWatching && productId === continueWatchingKeyword) {
            getItems(continueWatchingKeyword)
            setIsFetchContinuesWatching(false)
        }
    }, [isFetchContinuesWatching])

    useEffect(() => {
        if (onReady) onReady()
    }, [])

    const categoryStyle = {
        color: 'white',
        typography: 'headline3',
        marginBottom: '32px',
    }

    const rowViewContainerStyle = {
        marginBottom: '40px',
        position: 'relative',
        width: '99%',
        mt: '50px',
    }

    if (!rowItems) return <></>

    return (
        <FocusContext.Provider value={focusKey}>
            <Box
                ref={ref}
                onScroll={() => getItems()}
                sx={rowViewContainerStyle}>
                {!hideTitle && (
                    <Typography sx={categoryStyle}>{title}</Typography>
                )}

                {isLoading ? (
                    <VodContentSkeleton rows={1} items={6} />
                ) : rowItems.length > 0 ? (
                    <VodRowViewScrollingWrapper ref={scrollingRef}>
                        <VodRowViewScrollingContent>
                            {rowItems.map((item: VodItem, index: number) => (
                                <VodRowItemTV
                                    key={item.id}
                                    hideCaptions={hideCaptions}
                                    hideProgress={hideProgress}
                                    vodItemHeight={vodItemHeight}
                                    cardRadiusDp={cardRadiusDp}
                                    rowHeightPercentage={rowHeightPercentage}
                                    item={item}
                                    handleGoToCategoryButtonClick={
                                        handleGoToCategoryButtonClick
                                    }
                                    scrollPosition={scrollPosition}
                                    isHomeContentItem={isHomeContentRow}
                                    index={index}
                                    onFocusProp={onAssetFocus}
                                    onReady={onReady}
                                />
                            ))}
                        </VodRowViewScrollingContent>
                    </VodRowViewScrollingWrapper>
                ) : (
                    <Typography>{translate('lackOfChannels')}</Typography>
                )}
            </Box>
        </FocusContext.Provider>
    )
}

export default trackWindowScroll(VodRowTV)
